import { createAsyncThunk } from "@reduxjs/toolkit";
import apiCalls from "../../../PowerShadesAPIFunctions";
import { QuoteFabricSampleStore, QuoteFabricSampleVM } from "../../../powershadesApiTypes";
import { extraEntityReducersFunction, loadStatus } from "../entitiesType";
import { ThunkAPI } from "../types";
import { AxiosError } from "axios";
import { SelectFabrics } from "../fabrics";
import { AppState } from "../..";


const loadQuoteFabricSamples = createAsyncThunk<
QuoteFabricSampleVM[], number, ThunkAPI
>(
    'entities/GetFabricSamples',
    async (quoteId, { rejectWithValue }) => {
        let fabricSamples: QuoteFabricSampleVM[] = [];

        try {
            const resp = await apiCalls.getQuoteFabricSamples(quoteId);

            fabricSamples = resp.data.fabric_samples;
        } catch (err: any) {
            const error: AxiosError = err;

            console.error(error);

            return rejectWithValue(false);
        }

        return fabricSamples;
    }
);  

/**
 * Resolves the fabric name to send to the backend.
 *
 * Fabric names can legitimately contain hyphens (e.g. "ANASTASIA-126-DEEPENEDLEAD5-3200 mm"),
 * so the name must never be truncated at a hyphen. The sample dropdown historically appended
 * an "-{opacity}" suffix to the label, so if the raw name is not a known fabric we look for the
 * longest known fabric name that the raw name starts with (followed by "-"). Otherwise the name
 * is passed through untouched.
 */
const resolveFabricSampleName = (rawName: string, knownFabricNames: string[]): string => {
    const trimmed = (rawName ?? "").trim();

    if (trimmed.length === 0) return "";

    const exact = knownFabricNames.find((n) => n.trim() === trimmed);
    if (exact) return exact.trim();

    const prefixMatch = knownFabricNames
        .map((n) => n.trim())
        .filter((n) => n.length > 0 && trimmed.startsWith(`${n}-`))
        .sort((a, b) => b.length - a.length)[0];

    return prefixMatch ?? trimmed;
};

const addQuoteFabricSample = createAsyncThunk<
QuoteFabricSampleVM, { quoteId: number, name: string, quantity: number }, ThunkAPI
>(
    'entities/AddFabricSample',
    async ({ quoteId, name, quantity }, { rejectWithValue, getState }) => {
        let fabricSample;

        const getStateR = getState as () => AppState;

        const fabrics = SelectFabrics(quoteId)(getStateR());
        const fabName = resolveFabricSampleName(name, fabrics.map((f) => f.name));

        try {
            const resp = await apiCalls.addQuoteFabricSample(quoteId, {
                name: fabName,
                id: 0,
                quantity : quantity
            });

            fabricSample = resp.data;
        } catch (err: any) {
            const error: AxiosError = err;

            console.error(error);

            return rejectWithValue(false);
        }

        return fabricSample;
    }
);

const updateQuoteFabricSample = createAsyncThunk<
QuoteFabricSampleVM, { quoteId: number, name: string, quantity: number }, ThunkAPI
>(
    'entities/UpdateFabricSample',
    async ({ quoteId, name, quantity }, { rejectWithValue }) => {
        let fabricSample;

        try {
            const resp = await apiCalls.updateQuoteFabricSample(quoteId, name,quantity);

            fabricSample = resp.data.fabricSample;
        } catch (err: any) {
            const error: AxiosError = err;

            console.error(error);

            return rejectWithValue(false);
        }

        return fabricSample;
    }
);

const deleteQuoteFabricSample = createAsyncThunk<
boolean, { quoteId: number, name: string }, ThunkAPI
>(
    'entities/DeleteFabricSample',
    async ({ quoteId, name }, { rejectWithValue }) => {
        let success = false;

        try {
            const resp = await apiCalls.deleteQuoteFabricSample(quoteId, name);

            success = resp.data.success;
        } catch (err: any) {
            const error: AxiosError = err;

            console.error(error);

            return rejectWithValue(false);
        }

        return success;
    }
);


const quoteFabricSamplesBuilder: extraEntityReducersFunction = (builder) => {
    builder.addCase(loadQuoteFabricSamples.pending, (state, action) => {
        state.quoteFabricSamples[action.meta.arg] = {
            loadStatus:loadStatus.loading,
            Items: {}
        };
    });

    builder.addCase(loadQuoteFabricSamples.fulfilled, (state, action) => {
        state.quoteFabricSamples[action.meta.arg] = {
            loadStatus: loadStatus.fullyLoaded,
            Items: action.payload?.reduce((acc, item) => {
                acc[item.name] = {
                    Quantity: item.quantity,
                    Name: item.name,
                    Id: item.id
                };

                return acc;
            }, {} as {[key: string]: QuoteFabricSampleStore})
        };
    });
        
    builder.addCase(loadQuoteFabricSamples.rejected, (state, action) => {
        state.quoteFabricSamples[action.meta.arg] = {
            loadStatus: loadStatus.needsLoaded,
            Items: {}
        };
    });

    builder.addCase(addQuoteFabricSample.pending, (state, action) => {
        const qfRecord = state.quoteFabricSamples[action.meta.arg.quoteId];

        if(!qfRecord) {
            state.quoteFabricSamples[action.meta.arg.quoteId] = {
                loadStatus: loadStatus.loading,
                Items: {}
            };
        } else {
            qfRecord.loadStatus = loadStatus.loading;
        }

    });

    builder.addCase(addQuoteFabricSample.fulfilled, (state, action) => {
        const qfRecord = state.quoteFabricSamples[action.meta.arg.quoteId];

        const itemAdded = {
            Quantity: action.payload.quantity,
            Name: action.payload.name,
            Id: action.payload.id
        };

        if(qfRecord)
            {
                state.quoteFabricSamples[action.meta.arg.quoteId] = {
                    loadStatus: loadStatus.needsLoaded,
                    Items: {...qfRecord.Items, [action.payload.name]: itemAdded}
                };
            }
        
    });

    builder.addCase(addQuoteFabricSample.rejected, (state, action) => {
        const qfRecord = state.quoteFabricSamples[action.meta.arg.quoteId];

        if(qfRecord)
            {
                qfRecord.loadStatus = loadStatus.needsLoaded;
            }
    });

    builder.addCase(updateQuoteFabricSample.pending, (state, action) => {
        const qfRecord = state.quoteFabricSamples[action.meta.arg.quoteId];

        if(!qfRecord) {
            state.quoteFabricSamples[action.meta.arg.quoteId] = {
                loadStatus: loadStatus.loading,
                Items: {}
            };
        } else {
            qfRecord.loadStatus = loadStatus.loading;
        }

    });

    builder.addCase(updateQuoteFabricSample.fulfilled, (state, action) => {
        const qfRecord = state.quoteFabricSamples[action.meta.arg.quoteId];
        const itemUpdated = {
            Quantity: action.meta.arg.quantity,
            Name: action.meta.arg.name,
            Id: action?.payload?.id ?? 0
        };

        if(qfRecord)
            {
                state.quoteFabricSamples[action.meta.arg.quoteId] = {
                    loadStatus: loadStatus.needsLoaded,
                    Items: {...qfRecord.Items, [action.payload.name]: itemUpdated}
                };
            }
        
    });

    builder.addCase(updateQuoteFabricSample.rejected, (state, action) => {
        const qfRecord = state.quoteFabricSamples[action.meta.arg.quoteId];

        if(qfRecord)
            {
                qfRecord.loadStatus = loadStatus.needsLoaded;
            }
    });

    builder.addCase(deleteQuoteFabricSample.pending, (state, action) => {
        const qfRecord = state.quoteFabricSamples[action.meta.arg.quoteId];

        if(!qfRecord) {
            state.quoteFabricSamples[action.meta.arg.quoteId] = {
                loadStatus: loadStatus.loading,
                Items: {}
            };
        } else {
            qfRecord.loadStatus = loadStatus.loading;
        }

    });

    builder.addCase(deleteQuoteFabricSample.fulfilled, (state, action) => {
        const qfRecord = state.quoteFabricSamples[action.meta.arg.quoteId];

        if(qfRecord)
            {
                delete qfRecord.Items[action.meta.arg.name];
                qfRecord.loadStatus = loadStatus.needsLoaded;
            }
        
    });

    builder.addCase(deleteQuoteFabricSample.rejected, (state, action) => {
        const qfRecord = state.quoteFabricSamples[action.meta.arg.quoteId];

        if(qfRecord)
            {
                qfRecord.loadStatus = loadStatus.needsLoaded;
            }
    });

    
    
    return builder;


};

export {
    quoteFabricSamplesBuilder,
    addQuoteFabricSample,
    loadQuoteFabricSamples,
    updateQuoteFabricSample,
    deleteQuoteFabricSample
}